import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Truck, Bike, Store, Plus, Trash2, Save, Loader2, GripVertical, Package, Gift, ShoppingBag, Info, Layers } from "lucide-react";
import { toast } from "sonner";

const MODE_TYPES = [
  { id: "carrier", label: "Transporteur", Icon: Truck, description: "Colis Chronopost, Colissimo, Mondial Relay, DPD…" },
  { id: "courier", label: "Coursier", Icon: Bike, description: "Livraison locale rapide (1-3h) par coursier urbain" },
  { id: "internal", label: "Livraison interne", Icon: Store, description: "Votre propre véhicule / équipe sur votre zone d'intervention" },
];

const newMode = (type = "carrier") => ({
  id: `mode_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: type === "carrier" ? "Colissimo" : type === "courier" ? "Coursier express" : "Livraison interne",
  type,
  enabled: true,
  free_from_cents: 0,
  eta_label: type === "carrier" ? "Livraison sous 3-5 jours ouvrés" : type === "courier" ? "Livraison sous 2 heures" : "Livraison le jour même ou J+1",
  description: "",
  weight_tiers: [{ max_g: 1000, price_cents: 490 }, { max_g: 5000, price_cents: 890 }, { max_g: 30000, price_cents: 1490 }],
  zones: ["FR"],
});

export default function ShippingAdmin() {
  const { settings, refresh } = useSettings();
  const [modes, setModes] = useState([]);
  const [products, setProducts] = useState([]);
  const [giftRules, setGiftRules] = useState([]);
  const [foodMode, setFoodMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setModes(settings.shipping_modes || []);
    setGiftRules(settings.cart_gift_rules || []);
    setFoodMode(!!settings.food_mode_enabled);
    setDirty(false);
  }, [settings]);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data || [])).catch(() => setProducts([]));
  }, []);

  const patchMode = (idx, field, value) => {
    setModes(m => m.map((x, i) => i === idx ? { ...x, [field]: value } : x));
    setDirty(true);
  };
  const patchTier = (mi, ti, field, value) => {
    setModes(m => m.map((x, i) => {
      if (i !== mi) return x;
      const tiers = [...(x.weight_tiers || [])];
      tiers[ti] = { ...tiers[ti], [field]: value };
      return { ...x, weight_tiers: tiers };
    }));
    setDirty(true);
  };
  const addTier = (mi) => {
    setModes(m => m.map((x, i) => i === mi
      ? { ...x, weight_tiers: [...(x.weight_tiers || []), { max_g: 1000, price_cents: 500 }] }
      : x));
    setDirty(true);
  };
  const delTier = (mi, ti) => {
    setModes(m => m.map((x, i) => i === mi
      ? { ...x, weight_tiers: (x.weight_tiers || []).filter((_, j) => j !== ti) }
      : x));
    setDirty(true);
  };

  const addMode = (type) => {
    setModes([...modes, newMode(type)]);
    setDirty(true);
  };
  const delMode = (idx) => {
    if (!window.confirm("Supprimer ce mode de livraison ?")) return;
    setModes(modes.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addGift = () => {
    setGiftRules([...giftRules, {
      id: `gift_${Date.now()}`,
      threshold_cents: 5000,
      product_id: "",
      label: "Cadeau offert",
      enabled: true,
    }]);
    setDirty(true);
  };
  const patchGift = (idx, field, value) => {
    setGiftRules(r => r.map((x, i) => i === idx ? { ...x, [field]: value } : x));
    setDirty(true);
  };
  const delGift = (idx) => {
    setGiftRules(giftRules.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        shipping_modes: modes,
        cart_gift_rules: giftRules,
        food_mode_enabled: foodMode,
      });
      await refresh();
      setDirty(false);
      toast.success("Configuration sauvegardée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const fmt = cents => ((cents || 0) / 100).toFixed(2);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="shipping-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Truck className="w-3.5 h-3.5" /> Logistique & Food
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Livraison & paniers cadeaux</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Configurez vos modes de livraison avec paliers de poids et seuils de gratuité, vos cadeaux automatiques au panier, et activez les spécificités Food (allergènes, DLC, prix au kilo) si vous vendez du consommable.
          </p>
        </div>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="shipping-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {dirty ? "Enregistrer" : "Enregistré"}
        </Button>
      </header>

      {/* Mode Food */}
      <Card className="p-5 border-2 border-emerald-200 bg-emerald-50/30" data-testid="food-mode-card">
        <label className="flex items-start gap-3 cursor-pointer">
          <Switch checked={foodMode} onCheckedChange={v => { setFoodMode(v); setDirty(true); }} data-testid="food-mode-switch" />
          <div>
            <div className="font-bold text-[color:var(--brand-navy)]">Activer le mode Food / Restaurant</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 max-w-xl">
              Débloque sur les produits : allergènes EU (14 codes), liste d'ingrédients, DLC, origine, prix au kilo, conservation. S'affiche automatiquement sur les fiches produits marquées « alimentaire ».
            </p>
          </div>
        </label>
      </Card>

      {/* Modes de livraison */}
      <Card className="p-5 space-y-4" data-testid="shipping-modes-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-[color:var(--brand-sky)]" />
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Modes de livraison</h3>
          </div>
          <div className="flex items-center gap-1.5">
            {MODE_TYPES.map(t => (
              <Button key={t.id} variant="outline" size="sm" onClick={() => addMode(t.id)} className="text-xs" data-testid={`add-mode-${t.id}`}>
                <Plus className="w-3 h-3 mr-1" /> <t.Icon className="w-3.5 h-3.5 mr-0.5" /> {t.label}
              </Button>
            ))}
          </div>
        </div>

        {modes.length === 0 && (
          <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed">
            <Package className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-2 text-sm text-slate-600">Aucun mode de livraison configuré.</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Ajoutez-en un ci-dessus pour permettre l'expédition de vos commandes.</p>
          </div>
        )}

        <div className="space-y-3">
          {modes.map((mode, mi) => {
            const meta = MODE_TYPES.find(t => t.id === mode.type) || MODE_TYPES[0];
            return (
              <div key={mode.id} className="rounded-xl border overflow-hidden bg-white" data-testid={`shipping-mode-${mi}`}>
                <div className="px-4 py-3 flex items-center gap-3 bg-slate-50 border-b">
                  <meta.Icon className="w-4 h-4 text-[color:var(--brand-sky)]" />
                  <Input
                    value={mode.name}
                    onChange={e => patchMode(mi, "name", e.target.value)}
                    className="flex-1 h-8 text-sm font-bold"
                    placeholder="Nom du mode"
                    data-testid={`mode-name-${mi}`}
                  />
                  <Badge className="border-0 bg-slate-200 text-slate-700 text-[10px]">{meta.label}</Badge>
                  <Switch checked={mode.enabled !== false} onCheckedChange={v => patchMode(mi, "enabled", v)} />
                  <button onClick={() => delMode(mi)} className="p-1 text-red-500 hover:text-red-700" data-testid={`delete-mode-${mi}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold">Délai affiché</Label>
                      <Input
                        value={mode.eta_label || ""}
                        onChange={e => patchMode(mi, "eta_label", e.target.value)}
                        placeholder="Ex: Livraison sous 3-5 jours ouvrés"
                        className="mt-1 text-sm"
                        data-testid={`mode-eta-${mi}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold">Livraison gratuite à partir de (€)</Label>
                      <Input
                        type="number" step="0.01"
                        value={fmt(mode.free_from_cents)}
                        onChange={e => patchMode(mi, "free_from_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                        placeholder="0 pour désactiver"
                        className="mt-1 text-sm tabular-nums"
                        data-testid={`mode-freefrom-${mi}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold">Description (facultatif, visible au checkout)</Label>
                    <Textarea
                      rows={2}
                      value={mode.description || ""}
                      onChange={e => patchMode(mi, "description", e.target.value)}
                      placeholder="Instructions, zones couvertes, etc."
                      className="mt-1 text-xs"
                    />
                  </div>

                  {/* Weight tiers */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-bold flex items-center gap-1"><Layers className="w-3 h-3" /> Paliers de poids → tarif</Label>
                      <Button variant="outline" size="sm" onClick={() => addTier(mi)} className="text-[10px] h-6" data-testid={`mode-tier-add-${mi}`}>
                        <Plus className="w-2.5 h-2.5 mr-1" /> Palier
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      {(mode.weight_tiers || []).map((tier, ti) => (
                        <div key={ti} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50" data-testid={`mode-tier-${mi}-${ti}`}>
                          <span className="text-[11px] text-slate-500">Jusqu'à</span>
                          <Input
                            type="number"
                            value={tier.max_g || 0}
                            onChange={e => patchTier(mi, ti, "max_g", parseInt(e.target.value) || 0)}
                            className="w-24 h-7 text-xs text-right tabular-nums"
                          />
                          <span className="text-[11px] text-slate-500">g</span>
                          <span className="text-[11px] text-slate-500 mx-2">→</span>
                          <Input
                            type="number" step="0.01"
                            value={fmt(tier.price_cents)}
                            onChange={e => patchTier(mi, ti, "price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                            className="w-24 h-7 text-xs text-right tabular-nums"
                          />
                          <span className="text-[11px] text-slate-500">€</span>
                          <button onClick={() => delTier(mi, ti)} className="ml-auto p-1 text-red-400 hover:text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {(mode.weight_tiers || []).length === 0 && (
                        <p className="text-[11px] italic text-[hsl(var(--muted-foreground))] px-2">Aucun palier — ajoutez-en au moins un pour facturer la livraison.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Cart Gift rules */}
      <Card className="p-5 space-y-4" data-testid="gift-rules-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-[color:var(--brand-sky)]" />
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Cadeaux automatiques au panier</h3>
          </div>
          <Button variant="outline" size="sm" onClick={addGift} className="text-xs" data-testid="add-gift-rule">
            <Plus className="w-3 h-3 mr-1" /> Ajouter une règle
          </Button>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          Quand le total du panier dépasse le seuil, le produit sélectionné est ajouté automatiquement avec un prix de 0 € (non décompté du stock).
        </p>
        {giftRules.length === 0 && (
          <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed">
            <ShoppingBag className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-2 text-xs text-slate-500">Aucun cadeau configuré.</p>
          </div>
        )}
        <div className="space-y-2">
          {giftRules.map((g, gi) => (
            <div key={g.id} className="grid md:grid-cols-[auto_140px_1fr_120px_auto] gap-2 items-center p-2.5 rounded-lg border bg-white" data-testid={`gift-rule-${gi}`}>
              <Switch checked={g.enabled !== false} onCheckedChange={v => patchGift(gi, "enabled", v)} />
              <Input
                type="number" step="0.01"
                value={fmt(g.threshold_cents)}
                onChange={e => patchGift(gi, "threshold_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                placeholder="Seuil (€)"
                className="h-8 text-sm text-right tabular-nums"
                data-testid={`gift-threshold-${gi}`}
              />
              <select
                value={g.product_id || ""}
                onChange={e => patchGift(gi, "product_id", e.target.value)}
                className="h-8 rounded-md border px-2 text-sm bg-white"
                data-testid={`gift-product-${gi}`}
              >
                <option value="">— Choisir un produit offert —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Input
                value={g.label || ""}
                onChange={e => patchGift(gi, "label", e.target.value)}
                placeholder="Libellé affiché"
                className="h-8 text-sm"
              />
              <button onClick={() => delGift(gi)} className="p-1 text-red-500 hover:text-red-700">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
